import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Injector, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonRow,
  IonTitle,
  IonToggle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { IDecaDocument, isDecaServicePriorToTimestamp, UUID } from '@okcargo-apps/shared-domain';
import { DecaEditReasonComponent } from '../deca-edit-reason/deca-edit-reason.component';
import { DecaEditAuthorizationForm, DecaEditReasonFormModel } from '../deca-forms.model';
import { DecaListItemComponent } from '../deca-list-item/deca-list-item.component';
import { DecaMutationsService } from '../deca-mutations.service';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-edit-authorization-modal',
  templateUrl: './deca-edit-authorization-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonCol,
    IonRow,
    IonGrid,
    IonFooter,
    ReactiveFormsModule,
    IonContent,
    IonInput,
    IonItem,
    IonToggle,
    IonTitle,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    DecaEditReasonComponent,
    DecaListItemComponent,
  ],
})
export class DecaEditAuthorizationModalComponent implements OnInit {
  // comes from the component that opens the modal
  public readonly document!: IDecaDocument;
  public readonly userCompanyId!: UUID;

  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly decaMutationsService = inject(DecaMutationsService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form: FormGroup<DecaEditAuthorizationForm> = this.fb.group<DecaEditAuthorizationForm>({
    hasSpecialAuthorization: this.fb.nonNullable.control(false),
    specialAuthorizationType: this.fb.nonNullable.control(''),
    changeReason: this.fb.group<DecaEditReasonFormModel>({ reason: this.fb.nonNullable.control('') }),
  });

  protected get isServiceStarted(): boolean {
    return isDecaServicePriorToTimestamp(this.document, new Date());
  }

  private requireChangeReasonWhenServiceStarted(): void {
    if (!this.isServiceStarted) return;

    const reason = this.form.controls.changeReason.controls.reason;
    reason.addValidators(Validators.required);
    reason.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    this.requireChangeReasonWhenServiceStarted();
    const initialAuthType = this.document.specialAuthorizationType;
    const hasAuth = !!initialAuthType && initialAuthType.trim().length > 0;

    this.form.patchValue({
      hasSpecialAuthorization: hasAuth,
      specialAuthorizationType: hasAuth ? initialAuthType : '',
    });

    this.updateValidators(hasAuth);

    this.form.controls.hasSpecialAuthorization.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((requiresAuth) => {
        this.updateValidators(requiresAuth);
      });
  }

  private updateValidators(requiresAuth: boolean): void {
    const typeControl = this.form.controls.specialAuthorizationType;
    if (requiresAuth) {
      typeControl.setValidators([Validators.required, Validators.maxLength(100)]);
    } else {
      typeControl.clearValidators();
      typeControl.setValue('');
    }
    typeControl.updateValueAndValidity();
  }

  protected get errorText(): string {
    const control = this.form.controls.specialAuthorizationType;
    if (control.hasError('required')) return 'Debe especificar el tipo de autorización especial';
    if (control.hasError('maxlength')) return 'El texto no puede superar los 100 caracteres';
    return 'Dato no válido';
  }

  protected async saveChanges(): Promise<void> {
    if (this.form.invalid) {
      markFormAsInvalid(this.form, this.elementRef.nativeElement, this.injector);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: 'Enviando datos...',
    });
    await loading.present();

    try {
      const requiresAuth = this.form.controls.hasSpecialAuthorization.value;
      const rawType = this.form.controls.specialAuthorizationType.value.trim();

      const updatedDocument: IDecaDocument = {
        ...this.document,
        specialAuthorizationType: requiresAuth ? rawType : undefined,
      };

      await this.decaMutationsService.updateSpecialAuthorization(
        updatedDocument,
        this.form.controls.changeReason.controls.reason.value.trim(),
      );

      this.messagesUiService.setMessage({
        objectId: updatedDocument.id,
        message: ['Ha enviado una modificación sobre la autorización especial a este documento'],
        durationMs: 3000,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      this.decaStoreService.reloadAfterSaveData();

      this.modalCtrl.dismiss(updatedDocument, 'confirm');
    } catch (error) {
      // TODO manage error
      console.error('Error updating special authorization', error);
    } finally {
      await loading.dismiss();
    }
  }

  protected closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
